import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { BACKEND_URL } from '../config';

import {
  Check,
  CircleNotch,
  Link as LinkIcon,
  Trash,
  X
} from '@phosphor-icons/react';
const OpportunityLinkerModal = ({ isOpen, onClose, opportunity, onLinked }) => {
  const [processes, setProcesses] = useState([]);
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchProcesses();
      // Pre-select existing links if any
      if (opportunity?.process_id) {
        setSelectedProcessId(opportunity.process_id);
        fetchSteps(opportunity.process_id);
        if (opportunity?.process_step_id) {
          setSelectedStepId(opportunity.process_step_id);
        }
      }
    }
  }, [isOpen, opportunity]);

  useEffect(() => {
    if (selectedProcessId) {
      fetchSteps(selectedProcessId);
    } else {
      setSteps([]);
      setSelectedStepId(null);
    }
  }, [selectedProcessId]);

  const fetchProcesses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/org/processes`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch processes');
      }

      const data = await response.json();
      setProcesses(data);
    } catch (err) {
      console.error('Error fetching processes:', err);
      setError('Failed to load processes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSteps = async (processId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/org/processes/${processId}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch process steps');
      }

      const data = await response.json();
      setSteps(data.steps || []);
    } catch (err) {
      console.error('Error fetching process steps:', err);
      setSteps([]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/scanner/opportunities/${opportunity.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            process_id: selectedProcessId || null,
            process_step_id: selectedStepId || null
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to link opportunity');
      }

      // Notify parent component
      if (onLinked) {
        onLinked({
          ...opportunity,
          process_id: selectedProcessId,
          process_step_id: selectedStepId
        });
      }

      onClose();
    } catch (err) {
      console.error('Error linking opportunity:', err);
      setError(err.message || 'Failed to save link. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClearLink = async () => {
    setSelectedProcessId(null);
    setSelectedStepId(null);
    
    // If opportunity already has a link, save the clear immediately
    if (opportunity?.process_id) {
      await handleSave();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative glass rounded-3xl p-8 max-w-2xl w-full card-glow"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] transition-colors"
          >
            <X size={24} className="text-[#A0AEC0]" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#6366F1]/20 to-[#EC4899]/20 flex items-center justify-center mx-auto mb-4">
              <LinkIcon size={32} className="text-[#6366F1]" />
            </div>
            <h2 className="text-3xl font-bold text-white text-center mb-2">
              Link to <span className="gradient-text">Process</span>
            </h2>
            
            {/* Explainer Text */}
            <div className="mt-4 p-4 rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/30">
              <p className="text-sm text-[#A0AEC0] leading-relaxed">
                <span className="text-white font-semibold">Why link to a process?</span><br />
                Linking an opportunity to a mapped process step shows exactly where this automation lives in your workflow and keeps implementation work organised.
              </p>
            </div>
          </div>

          {/* Opportunity Info */}
          <div className="glass rounded-xl p-4 mb-6">
            <p className="text-sm text-[#718096] mb-1">Linking Opportunity:</p>
            <p className="text-white font-semibold">{opportunity?.title}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <CircleNotch size={32} className="text-[#6366F1] animate-spin" />
            </div>
          ) : (
            <div className="space-y-6 mb-8">
              {/* Process Dropdown */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Process <span className="text-[#EF4444]">*</span>
                </label>
                <select
                  value={selectedProcessId || ''}
                  onChange={(e) => setSelectedProcessId(e.target.value || null)}
                  className="w-full px-4 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-all"
                  disabled={processes.length === 0}
                >
                  <option value="">-- Select a process --</option>
                  {processes.map((process) => (
                    <option key={process.id} value={process.id} className="bg-[#1A1B23] text-white">
                      {process.name}
                    </option>
                  ))}
                </select>
                {processes.length === 0 && (
                  <p className="text-sm text-[#718096] mt-2">
                    No processes found. Create a process first in the Process Library.
                  </p>
                )}
              </div>

              {/* Step Dropdown */}
              {selectedProcessId && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <label className="block text-sm font-medium text-white mb-2">
                    Step <span className="text-[#718096]">(optional)</span>
                  </label>
                  <select
                    value={selectedStepId || ''}
                    onChange={(e) => setSelectedStepId(e.target.value || null)}
                    className="w-full px-4 py-3 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1] transition-all"
                    disabled={steps.length === 0}
                  >
                    <option value="">-- Select a step (optional) --</option>
                    {steps.map((step) => (
                      <option key={step.id} value={step.id} className="bg-[#1A1B23] text-white">
                        {step.order_index}. {step.title}
                      </option>
                    ))}
                  </select>
                  {steps.length === 0 && (
                    <p className="text-sm text-[#718096] mt-2">
                      This process has no steps yet.
                    </p>
                  )}
                </motion.div>
              )}

              {/* Current Link Status */}
              {opportunity?.process_id && (
                <div className="p-4 rounded-xl bg-[#14F195]/10 border border-[#14F195]/30">
                  <p className="text-sm text-[#14F195] flex items-center gap-2">
                    <Check size={16} />
                    Currently linked to a process
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saving || loading || !selectedProcessId}
              className="flex-1 relative overflow-hidden rounded-full group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-[#14F195] via-[#6366F1] to-[#EC4899]"></div>
              <span className="relative z-10 flex items-center justify-center gap-2 py-3 text-white font-bold">
                {saving ? (
                  <>
                    <CircleNotch size={20} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={20} />
                    Save Link
                  </>
                )}
              </span>
            </button>

            {/* Clear Link Button */}
            {(opportunity?.process_id || selectedProcessId) && (
              <button
                onClick={handleClearLink}
                disabled={saving || loading}
                className="px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-[#EF4444] font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Trash size={20} />
                Clear Link
              </button>
            )}

            {/* Cancel Button */}
            <button
              onClick={onClose}
              disabled={saving}
              className="px-6 py-3 rounded-full glass hover:bg-[rgba(255,255,255,0.1)] text-white font-semibold transition-all disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default OpportunityLinkerModal;
